// Packages
import {
  BrowserRouter,
  Link,
  Routes,
  Route,
  useLocation,
  useNavigate,
  Navigate,
} from "react-router-dom";
import { lazy, Suspense, useState, useEffect } from "react";
import { Provider } from "react-redux";
import { registerServiceWorker } from "@jkast90/kast-common/offline";
import {
  AppShell,
  LinkProvider,
  SiteFooter,
  Button,
  TooltipHost,
  buttonClass,
  useKeyboardShortcuts,
  useMobileView,
} from "@jkast90/kast-common/ui";

// Redux Store
import { store } from "@common/store/store";
import { initializeCacheListener } from "@common/store/slices/cacheSlice";

// Contexts
import { AppProviders } from "@common/components/AppProviders";

// Pages
import Auth from "@common/pages/Auth";
import BugReport from "@common/pages/BugReport";
import { NavBar, OfflineBanner } from "@common/components/layout";
import HomeSettings from "@common/components/layout/HomeSettings";
import { ProtectedRoute } from "@common/components/auth";
import { AccountPage } from "@common/accountKit";
import RouteHandler from "@common/components/RouteHandler";
import RouteTracker from "@common/components/RouteTracker";
import WorkspaceUrlSync from "@common/components/WorkspaceUrlSync";

// The signed-in pages and the help dialog load on first use, each its own
// chunk, so the first paint downloads only the shell.
const Links = lazy(() => import("@links/pages/Links"));
const Notes = lazy(() => import("@notes/pages/Notes"));
const Threads = lazy(() => import("@threads/pages/Threads"));
const Browser = lazy(() => import("@browser/pages/Browser"));
const About = lazy(() => import("@about/pages/About"));
const AdminPage = lazy(() => import("@settings/pages/AdminPage"));
const WorkspaceView = lazy(() => import("@workspace/pages/WorkspaceView"));
const Reference = lazy(() => import("@reference/pages/Reference"));
const InstructionsDialog = lazy(
  () => import("@help/components/InstructionsDialog"),
);

// Error Handling
import ErrorBoundary from "@jkast90/kast-common/error-boundary";

// Styling
import "./App.css";

// Utils
import {
  installGlobalErrorHandlers,
  logger,
} from "@jkast90/kast-common/diagnostics";

function AppWithPrompt() {
  const location = useLocation();
  const navigate = useNavigate();
  const [showInstructions, setShowInstructions] = useState(false);
  // The help dialog's chunk loads the first time it opens; after that it
  // stays mounted, as it always was.
  const [instructionsWanted, setInstructionsWanted] = useState(false);
  const openInstructions = () => {
    setInstructionsWanted(true);
    setShowInstructions(true);
  };
  const [tourMode, setTourMode] = useState(false);

  // The sign-in hash from login.kast-dev.net is taken before the first
  // render, where the session is created (auth/session.ts).

  // The mobile view and the fixed-width preview: this is the one mounted
  // hook that applies them to <body>; the rest read them with applyToBody
  // false.
  useMobileView();

  useEffect(() => {
    // Uncaught errors and unhandled rejections go to the diagnostics logger,
    // so they reach a bug report.
    const removeErrorHandlers = installGlobalErrorHandlers();
    const unsubscribe = initializeCacheListener(store.dispatch);
    return () => {
      removeErrorHandlers();
      unsubscribe();
    };
  }, []);

  // "?" opens the instructions (Shift+/ on most keyboards), unless the user
  // is typing.
  useKeyboardShortcuts({
    "?": () => {
      setInstructionsWanted(true);
      setShowInstructions(true);
    },
  });

  // The worker is a build artifact (offlineServiceWorker's plugin in
  // vite.config.js), so the dev server has nothing to register; a worker left
  // over from an earlier run is removed so dev never serves stale cached
  // files. A deploy takes over by itself: the worker script is served
  // no-store and fetched past the HTTP cache, a new one installs under new
  // cache names, skips waiting, claims open tabs and deletes the old build's
  // caches. Navigations are network-first, so the next load online is always
  // the new shell; offline it is the newest one installed.
  useEffect(() => {
    registerServiceWorker({ enabled: import.meta.env.PROD });
  }, []);

  return (
    <>
      {/* Every element with data-tip (the Tip wrapper) gets its tooltip. */}
      <TooltipHost />
      <RouteTracker />
      <WorkspaceUrlSync />
      {/* The nav bar is fixed: the shell pads its top by --kui-nav-height,
          which the bar sets to its measured height (it wraps on a phone). */}
      <AppShell
        fixedHeader
        header={
          <>
            <NavBar onHelpClick={openInstructions} />
            <OfflineBanner />
          </>
        }
        footer={<SiteFooter collapsible={false} />}
      >
        {/* Inside the content area, keyed on the route's section (the first
          path segment): the NavBar and Footer stay usable after a crash,
          moving to another section clears it, and a change within a section
          (a workspace id, a slug) doesn't remount the page. */}
        <ErrorBoundary
          key={location.pathname.split("/")[1]}
          onError={(error, info) =>
            logger.error(`[Crash] ${error.message}`, info.componentStack ?? "")
          }
          onReport={(crashNote) =>
            navigate("/bug-report", { state: { crashNote } })
          }
          buttonClassName={buttonClass({ variant: "secondary" })}
          primaryButtonClassName={buttonClass({ variant: "primary" })}
        >
          <Suspense fallback={null}>
            <Routes>
              <Route path="/auth" element={<Auth />} />
              <Route path="/bug-report" element={<BugReport />} />
              <Route path="/account" element={<AccountPage />} />
              <Route
                path="/admin"
                element={<Navigate to="/admin/visitors" replace />}
              />
              <Route
                path="/admin/:tab"
                element={
                  <ProtectedRoute needsWorkspaces={false}>
                    <AdminPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/"
                element={
                  <ProtectedRoute>
                    <RouteHandler />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/links/:workspaceId?"
                element={
                  <ProtectedRoute>
                    <Links />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/notes/:workspaceId?"
                element={
                  <ProtectedRoute>
                    <Notes />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/workspace/:workspaceId?"
                element={
                  <ProtectedRoute>
                    <WorkspaceView />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/threads/:workspaceId?"
                element={
                  <ProtectedRoute>
                    <Threads />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/browse/:workspaceId?"
                element={
                  <ProtectedRoute>
                    <Browser />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/about/:workspaceId?"
                element={
                  <ProtectedRoute>
                    <About />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/reference"
                element={
                  <ProtectedRoute needsWorkspaces={false}>
                    <Reference />
                  </ProtectedRoute>
                }
              />
              <Route
                path="*"
                element={
                  <div
                    style={{
                      padding: "2rem",
                      textAlign: "center",
                      color: "var(--text)",
                      backgroundColor: "var(--surface)",
                      margin: "2rem",
                      borderRadius: "8px",
                      border: "1px solid var(--accent-dark)",
                      boxShadow: "0 2px 8px var(--shadow-color)",
                    }}
                  >
                    <h1 style={{ color: "var(--text)", marginBottom: "1rem" }}>
                      404 – Page Not Found
                    </h1>
                    <p
                      style={{
                        color: "var(--text-muted)",
                        marginBottom: "1.5rem",
                      }}
                    >
                      The page you're looking for doesn't exist.
                    </p>
                    <Button
                      variant="primary"
                      onClick={() => window.history.back()}
                      style={{ marginRight: "0.5rem" }}
                    >
                      Go Back
                    </Button>
                    <Button as="a" href="/">
                      Home
                    </Button>
                  </div>
                }
              />
            </Routes>
          </Suspense>
        </ErrorBoundary>
      </AppShell>
      <HomeSettings />
      {instructionsWanted && (
        <Suspense fallback={null}>
          <InstructionsDialog
            open={showInstructions}
            onClose={() => {
              setShowInstructions(false);
              setTourMode(false);
            }}
            tourMode={tourMode}
            onStartTour={() => {
              setTourMode(true);
            }}
          />
        </Suspense>
      )}
    </>
  );
}

export function AppContent() {
  return <AppWithPrompt />;
}

export default function App() {
  return (
    <Provider store={store}>
      <AppProviders>
        <BrowserRouter>
          <LinkProvider link={Link}>
            <AppContent />
          </LinkProvider>
        </BrowserRouter>
      </AppProviders>
    </Provider>
  );
}
